import { Route } from 'react-router-dom';

import { AuthGuard } from 'components/AuthGuard';
import { MainLayout } from 'components/Layout';
import { DriverRoutes } from 'features/drivers/routes';
import { lazyImport } from 'utils/lazyImport';

import { AdminRouteGuard } from './AdminRouteGuard';
import { AppRouteConfig } from './appRouteConfig';
import { CustomerRouteGuard } from './CustomerRouteGuard';
import { AsyncStatusRoutes } from 'features/operations';

const { AddressBooks } = lazyImport(
  () => import('features/records/routes/customers/AddressBooks'),
  'AddressBooks'
);

const { CustomerPortalConfiguration } = lazyImport(
  () =>
    import(
      'features/customerPortal/components/CustomerPortalConfiguration/CustomerPortalConfiguration'
    ),
  'CustomerPortalConfiguration'
);
const { DegreeDaysRoutes } = lazyImport(
  () => import('features/degreeDays/routes/DegreeDaysRoutes'),
  'DegreeDaysRoutes'
);

const { OperationsRoutes } = lazyImport(() => import('features/operations'), 'OperationsRoutes');

const { AllCustomerAssets } = lazyImport(
  () => import('features/records/routes/customers/AllCustomerAssets'),
  'AllCustomerAssets'
);
const { SettingRoutes } = lazyImport(() => import('features/settings/routes'), 'SettingRoutes');
const { TankMonitorRoutes } = lazyImport(
  () => import('features/tankMonitor/routes'),
  'TankMonitorRoutes'
);

const { DataImportRoutes } = lazyImport(
  () => import('features/dataImport/routes'),
  'DataImportRoutes'
);
const { RecordRoutes } = lazyImport(() => import('features/records'), 'RecordRoutes');
const { PlaceholderRoutes } = lazyImport(() => import('features/placeholder'), 'PlaceholderRoutes');
const { ShiftRoutes } = lazyImport(() => import('features/routing'), 'ShiftRoutes');
const { OrdersRoutes } = lazyImport(() => import('features/orders'), 'OrdersRoutes');
const { DashboardRoutes } = lazyImport(() => import('features/dashboard.new'), 'DashboardRoutes');
const { DeliveryOrders } = lazyImport(
  () => import('features/dashboard.new/components/DeliveryOrders'),
  'DeliveryOrders'
);
const { RecurringOrderPage } = lazyImport(
  () => import('features/dashboard.new/pages/RecurringOrderPage'),
  'RecurringOrderPage'
);
const { ShiftBoardRoutes } = lazyImport(() => import('features/shifts'), 'ShiftBoardRoutes');
const { ProfileRoutes } = lazyImport(() => import('features/profile'), 'ProfileRoutes');
const { StaffRoutes } = lazyImport(() => import('features/staff'), 'StaffRoutes');

const { Graph } = lazyImport(
  () => import('features/dashboard.new/components/Graph/Graph'),
  'Graph'
);
const { PricingRoutes } = lazyImport(() => import('features/pricing'), 'PricingRoutes');
const { Dashboard } = lazyImport(() => import('javascript/containers/Dashboard'), 'Dashboard');
const { QrCodeList } = lazyImport(
  () => import('javascript/containers/dispatch/qr_codes'),
  'QrCodeList'
);
const { ModalTypeDetailPage } = lazyImport(
  () => import('javascript/containers/Shifts/components/Modal/ModalTypeDetailPage'),
  'ModalTypeDetailPage'
);

const { DispatchRoutes } = lazyImport(() => import('features/dispatch'), 'DispatchRoutes');

const { ReportRoutes } = lazyImport(() => import('features/reports/routes'), 'ReportRoutes');

const { DeliveryTypeOrderDetailPage } = lazyImport(
  () => import('features/orderDetails/pages/DeliveryTypeOrderDetailPage'),
  'DeliveryTypeOrderDetailPage'
);

const { PandaBoxVersion } = lazyImport(
  () => import('features/records/routes/self-customer/PandaBox/PandaBoxVersion'),
  'PandaBoxVersion'
);
const { MobileVersion } = lazyImport(
  () => import('features/records/routes/self-customer/PandaBox/MobileVersion'),
  'MobileVersion'
);
const { PandaBoxLogs } = lazyImport(
  () => import('features/records/routes/self-customer/PandaBox/PandaBoxLogs/PandaBoxLogs'),
  'PandaBoxLogs'
);

const ORDER_DETAILS = {
  delivery: <DeliveryTypeOrderDetailPage />,
};

export const privateRoutes = (
  <>
    <Route path="*" element={<AuthGuard />}>
      <Route path="*" element={<MainLayout />}>
        <Route
          key={'detailModalFromDelivery'}
          path={`shift-details/:id`}
          element={<ModalTypeDetailPage />}
        />
        {(['delivery'] as const).map((path) => {
          return <Route key={path} path={`${path}/:id`} element={ORDER_DETAILS[path]} />;
        })}
        {(['customers', 'self_customer'] as const).map((path) => (
          <Route key={path} path={`${path}/*`} element={<RecordRoutes which={path} />} />
        ))}
        {(['route_config', 'lea_old', 'lea'] as const).map((path) => (
          <Route key={path} path={`${path}/*`} element={<OperationsRoutes which={path} />} />
        ))}
        {(['async_status'] as const).map((path) => (
          <Route key={path} path={`${path}/*`} element={<AsyncStatusRoutes which={path} />} />
        ))}
        <Route path="" element={<Dashboard />} />
        <Route path={AppRouteConfig.DRIVERS_TIME_SPENT.path} element={<DriverRoutes />} />
        <Route path="dispatch/*" element={<DispatchRoutes />} />
        <Route path="orders/*" element={<OrdersRoutes />} />
        <Route path="data-import/*" element={<DataImportRoutes />} />
        <Route path="pricing/*" element={<PricingRoutes />} />
        <Route path="customer_assets" element={<AllCustomerAssets />} />
        <Route path="point_of_contacts" element={<AddressBooks />} />
        <Route path="shift_plans/*" element={<ShiftRoutes />} />
        <Route path="reports/*" element={<ReportRoutes />} />
        <Route
          path="settings/*"
          element={
            <AdminRouteGuard>
              <SettingRoutes />
            </AdminRouteGuard>
          }
        />
        <Route path="dashboard/*" element={<Dashboard />} />

        <Route
          path="profile/*"
          element={
            <CustomerRouteGuard>
              <ProfileRoutes />
            </CustomerRouteGuard>
          }
        />
        {/* we may remove this later on start */}
        {/* TODO: Move this route to DashboardRoutes and rename DashboardRoutes component to CustomerPortalROutes÷ */}
        <Route path="customer-portal/dashboard" element={<DashboardRoutes />} />
        <Route path="customer-portal/delivery-orders" element={<DeliveryOrders />} />
        <Route path="customer-portal/recurring-orders" element={<RecurringOrderPage />} />
        <Route path="customer-portal/graph" element={<Graph />} />
        <Route path="customer-portal-configuration" element={<CustomerPortalConfiguration />} />
        <Route path="tank_monitors/*" element={<TankMonitorRoutes />} />
        <Route path="staff/*" element={<StaffRoutes />} />
        <Route path="dispatch/qr_codes/all" element={<QrCodeList />} />
        {/* we may remove this later on end */}
        <Route path="*" element={<PlaceholderRoutes />} />
        <Route path="shift_boards/*" element={<ShiftBoardRoutes />} />
        <Route path="pandabox-version" element={<PandaBoxVersion />} />
        <Route path="mobile-version" element={<MobileVersion />} />
        <Route path="pandabox-logs" element={<PandaBoxLogs />} />
        <Route path="degree-days/*" element={<DegreeDaysRoutes />} />
      </Route>
    </Route>
  </>
);
